import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.tsx';
import DonationModal from './DonationModal';
import '../styles/TrailBuddyForm.css';

export default function TrailBuddyForm({ trail, nameRegion, onClose, onSuccess }) {
    const { t } = useTranslation();
    const { user, isAuthenticated, loginWithGoogle, logout, loading: authLoading } = useAuth();

    const [formData, setFormData] = useState({
        eventDate: '',
        meetingTime: '',
        meetingPoint: '',
        lookingFor: '',
        organizerPhone: '',
        notes: '',
        isRecurring: false,
        recurringDays: [],
        recurringEndDate: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [error, setError] = useState(null);
    const [showDonation, setShowDonation] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const WEEKDAYS = [
        { id: 1, short: 'Lun', full: 'Lunedì' },
        { id: 2, short: 'Mar', full: 'Martedì' },
        { id: 3, short: 'Mer', full: 'Mercoledì' },
        { id: 4, short: 'Gio', full: 'Giovedì' },
        { id: 5, short: 'Ven', full: 'Venerdì' },
        { id: 6, short: 'Sab', full: 'Sabato' },
        { id: 0, short: 'Dom', full: 'Domenica' }
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name === 'isRecurring') {
            setFormData(prev => ({
                ...prev,
                isRecurring: checked,
                recurringDays: checked ? prev.recurringDays : [],
                recurringEndDate: checked ? prev.recurringEndDate : ''
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const toggleWeekday = (dayId) => {
        setFormData(prev => {
            const days = prev.recurringDays.includes(dayId)
                ? prev.recurringDays.filter(d => d !== dayId)
                : [...prev.recurringDays, dayId];
            return { ...prev, recurringDays: days };
        });
    };

    const generateRecurringDates = (startDate, endDate, selectedDays) => {
        const dates = [];
        let current = new Date(startDate);
        const end = new Date(endDate);
        while (current <= end) {
            if (selectedDays.includes(current.getDay())) {
                dates.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const handleGoogleLogin = async () => {
        setIsLoggingIn(true);
        setError(null);
        try {
            await loginWithGoogle();
        } catch (err) {
            setError('Login Google fallito. Riprova.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAuthenticated) {
            setError('Devi accedere con Google prima di pubblicare');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        if (formData.isRecurring) {
            if (formData.recurringDays.length === 0) {
                setError('Seleziona almeno un giorno della settimana per la ricorrenza');
                setIsSubmitting(false);
                return;
            }
            if (!formData.recurringEndDate) {
                setError('Specifica una data di fine per l\'evento ricorrente');
                setIsSubmitting(false);
                return;
            }
            if (new Date(formData.recurringEndDate) <= new Date(formData.eventDate)) {
                setError('La data di fine deve essere successiva alla data di inizio');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002';

            let datesToCreate = [formData.eventDate];
            if (formData.isRecurring) {
                datesToCreate = generateRecurringDates(
                    formData.eventDate,
                    formData.recurringEndDate,
                    formData.recurringDays
                ).map(d => d.toISOString().split('T')[0]);
            }

            const baseEventPayload = {
                eventName: `Cerca compagni: ${trail.properties?.name || 'Percorso'}`,
                meetingPoint: formData.meetingPoint,
                meetingTime: formData.meetingTime,
                lookingFor: formData.lookingFor ? Number(formData.lookingFor) : null,
                organizerPhone: formData.organizerPhone || '',
                notes: formData.notes,
                trail: {
                    id: trail.properties?.id || trail.id,
                    name: trail.properties?.name || 'Percorso senza nome',
                    region: nameRegion,
                    length: trail.properties?.lengthKm || trail.properties?.length,
                    ascent: trail.properties?.ascentMeters || trail.properties?.ascent,
                    difficulty: trail.properties?.difficulty
                },
                isRecurring: formData.isRecurring,
                recurrence: formData.isRecurring ? { dates: datesToCreate } : null
            };

            const token = await user.getIdToken();

            const response = await fetch(`${apiUrl}/api/trail-buddy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...baseEventPayload,
                    eventDate: datesToCreate[0]
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Errore durante l\'invio');
            }

            await response.json();

            // Mostra modal donazione prima di notificare il genitore
            setSuccessMessage(formData.isRecurring
                ? `✅ ${datesToCreate.length} ricorrenze create con successo!`
                : '✅ Cerca compagni percorso pubblicato!'
            );
            setShowDonation(true);
            // onSuccess viene chiamato alla chiusura del modal

        } catch (err) {
            setError(err.message === 'Failed to fetch'
                ? 'Impossibile connettersi al server. Verifica che il backend sia attivo.'
                : err.message || 'Errore durante l\'invio. Riprova più tardi.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="trail-buddy-overlay">
                <div className="trail-buddy-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="trail-buddy-header">
                        <h2>🥾👥 {t('find_buddies_for_track')}</h2>
                        <button className="close-button" onClick={onClose}>✕</button>
                    </div>

                    <div className="trail-info">
                        <h3>📍 {trail.properties?.name || 'Percorso selezionato'}</h3>
                        <div className="trail-details">
                            <span>📏 {trail.properties?.length || 'N/A'}</span>
                            <span>⛰️ {trail.properties?.ascent || 'N/A'}</span>
                            <span>⏱️ {trail.properties?.duration || 'N/A'}</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="trail-buddy-form">
                        {/* Quando */}
                        <div className="form-section">
                            <h3>📅 Quando?</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="eventDate">
                                        {formData.isRecurring ? 'Data Inizio *' : 'Data Escursione *'}
                                    </label>
                                    <input
                                        type="date"
                                        id="eventDate"
                                        name="eventDate"
                                        value={formData.eventDate}
                                        onChange={handleChange}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="meetingTime">Orario Ritrovo *</label>
                                    <input
                                        type="time"
                                        id="meetingTime"
                                        name="meetingTime"
                                        value={formData.meetingTime}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isRecurring"
                                        checked={formData.isRecurring}
                                        onChange={handleChange}
                                    />
                                    <span>🔄 Ripeti settimanalmente</span>
                                </label>
                            </div>

                            {formData.isRecurring && (
                                <div className="recurring-section">
                                    <div className="form-group">
                                        <label>Ripeti ogni: *</label>
                                        <div className="weekday-selector">
                                            {WEEKDAYS.map(day => (
                                                <button
                                                    key={day.id}
                                                    type="button"
                                                    className={`weekday-btn ${formData.recurringDays.includes(day.id) ? 'selected' : ''}`}
                                                    onClick={() => toggleWeekday(day.id)}
                                                    title={day.full}
                                                >
                                                    {day.short}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="recurringEndDate">Fino al: *</label>
                                        <input
                                            type="date"
                                            id="recurringEndDate"
                                            name="recurringEndDate"
                                            value={formData.recurringEndDate}
                                            onChange={handleChange}
                                            min={formData.eventDate || new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Dove */}
                        <div className="form-section">
                            <h3>📍 Dove?</h3>
                            <div className="form-group">
                                <label htmlFor="meetingPoint">Punto di Ritrovo *</label>
                                <input
                                    type="text"
                                    id="meetingPoint"
                                    name="meetingPoint"
                                    value={formData.meetingPoint}
                                    onChange={handleChange}
                                    placeholder="es. Parcheggio Rifugio XYZ"
                                    required
                                />
                            </div>
                        </div>

                        {/* Quante persone */}
                        <div className="form-section">
                            <h3>👥 Quante persone cerchi?</h3>
                            <div className="form-group">
                                <label htmlFor="lookingFor">Numero Persone</label>
                                <select id="lookingFor" name="lookingFor" value={formData.lookingFor} onChange={handleChange}>
                                    <option value="">Qualsiasi numero</option>
                                    <option value="1">1 persona</option>
                                    <option value="2">2 persone</option>
                                    <option value="3">3 persone</option>
                                    <option value="4">4 persone</option>
                                    <option value="5">5+ persone</option>
                                </select>
                            </div>
                        </div>

                        {/* Account */}
                        <div className="form-section">
                            <h3>👤 Il Tuo Account</h3>
                            {authLoading ? (
                                <div className="google-auth-loading"><span>⏳ Caricamento...</span></div>
                            ) : isAuthenticated && user ? (
                                <div className="google-user-info">
                                    <div className="google-user-card">
                                        {user.photoURL && (
                                            <img src={user.photoURL} alt="Avatar" className="google-avatar" referrerPolicy="no-referrer" />
                                        )}
                                        <div className="google-user-details">
                                            <span className="google-user-name">{user.displayName || 'Utente Google'}</span>
                                            <div />
                                            <span className="google-user-email">{user.email}</span>
                                        </div>
                                        <button type="button" className="btn-logout" onClick={logout} title="Disconnetti">✕</button>
                                    </div>
                                    <div className="form-group" style={{ marginTop: '12px' }}>
                                        <label htmlFor="organizerPhone">📱 Telefono / WhatsApp (opzionale)</label>
                                        <input
                                            type="tel"
                                            id="organizerPhone"
                                            name="organizerPhone"
                                            value={formData.organizerPhone}
                                            onChange={handleChange}
                                            placeholder="+39 123 456 7890"
                                        />
                                        <small style={{ color: '#888', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                            Visibile solo ai partecipanti accettati
                                        </small>
                                    </div>
                                    <p className="privacy-note">🔒 I tuoi contatti non saranno visibili pubblicamente</p>
                                </div>
                            ) : (
                                <div className="google-login-section">
                                    <p className="google-login-hint">
                                        Accedi con Google per pubblicare il tuo annuncio.<br />
                                        <small>Nome e email verranno compilati automaticamente.</small>
                                    </p>
                                    <button type="button" className="btn-google-login" onClick={handleGoogleLogin} disabled={isLoggingIn}>
                                        {isLoggingIn ? '⏳ Accesso in corso...' : (
                                            <>
                                                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                                </svg>
                                                Accedi con Google
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Note */}
                        <div className="form-section">
                            <h3>📝 Note</h3>
                            <div className="form-group">
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Info aggiuntive: livello richiesto, cosa portare, ecc."
                                />
                            </div>
                        </div>

                        {error && <div className="error-message">⚠️ {error}</div>}
                        <div className="form-actions">
                            <button type="button" onClick={onClose} className="btn-cancel">Annulla</button>
                            <button
                                type="submit"
                                disabled={isSubmitting || !isAuthenticated}
                                className={`btn-submit ${!isAuthenticated ? 'btn-disabled' : ''}`}
                                title={!isAuthenticated ? 'Accedi con Google per pubblicare' : ''}
                            >
                                {isSubmitting ? '⏳ Pubblicazione...'
                                    : !isAuthenticated ? '🔒 Accedi per Pubblicare'
                                        : formData.isRecurring ? '🚀 Pubblica Ricorrente'
                                            : '🚀 Pubblica'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Modal donazione dopo successo */}
                {showDonation && (
                    <DonationModal
                        message={successMessage}
                        onClose={() => {
                            setShowDonation(false);
                            if (onSuccess) onSuccess([]);
                            onClose();
                        }}
                    />
                )}
            </div>
        </>
    );
}
